import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { fetchArticles } from "../lib/api";
import ArticleCard from "../components/ArticleCard";
import { CATEGORIES } from "../lib/brand";
import SEO from "../components/SEO";

export default function Editorial() {
  const [category, setCategory] = useState("all");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    setLoading(true);
    const params = { limit: 100 };
    if (category !== "all") params.category = category;
    if (debouncedQ.trim()) params.q = debouncedQ.trim();
    fetchArticles(params)
      .then((r) => setArticles(r.articles || []))
      .finally(() => setLoading(false));
  }, [category, debouncedQ]);

  return (
    <div data-testid="editorial-page">
      <SEO
        title="Editorial Hub"
        description="Game recaps, draft boards, and culture takes from the locker room to the timeline. The searchable archive of every Unfiltered & On-Ball story."
      />
      {/* Header band */}
      <section className="border-b-2 border-[var(--ink)]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-12 md:py-16">
          <div className="text-kicker">Editorial Hub</div>
          <h1 className="text-headline text-5xl md:text-7xl lg:text-8xl mt-2">
            On-Ball <span className="text-[var(--rust)]">/</span> In Print
          </h1>
          <p className="text-dek text-lg md:text-xl mt-4 max-w-2xl">
            Game recaps, draft boards, and culture takes from the locker room to the timeline. Searchable archive of every story we've filed.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="sticky top-[73px] md:top-[85px] z-30 bg-[var(--paper)] border-b-2 border-[var(--ink)]" data-testid="editorial-filters">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-4 flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
            {CATEGORIES.map((c) => (
              <button key={c.key}
                      onClick={() => setCategory(c.key)}
                      className={`chip whitespace-nowrap ${category === c.key ? "chip-active" : ""}`}
                      data-testid={`category-filter-${c.key}`}>
                {c.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 border-2 border-[var(--ink)] px-3 py-2 bg-[var(--paper)] md:w-80">
            <Search size={16} className="text-[var(--muted)]" />
            <input type="text" placeholder="Search recaps, teams, topics…"
                   value={q} onChange={(e) => setQ(e.target.value)}
                   className="flex-1 bg-transparent outline-none text-sm"
                   data-testid="editorial-search-input" />
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="py-12" data-testid="editorial-grid">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8">
          {loading ? (
            <div className="text-center py-20 font-mono-ed uppercase tracking-widest text-[var(--muted)]">Loading archive…</div>
          ) : articles.length === 0 ? (
            <div className="text-center py-20">
              <div className="text-headline text-3xl text-[var(--ink)]">No stories match.</div>
              <p className="text-dek mt-2">Try another category or clear your search.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
              {articles.map((a) => (
                <ArticleCard key={a.slug} article={a} size="md" />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
